/**
 * Gmail-style inline translation for conversation messages.
 *
 * When translation is active, the translated text replaces the original
 * content IN PLACE — never appended below. A compact banner sits above
 * the content with language info, a settings gear, and a "Show original"
 * toggle. Only one version (original or translated) is visible at a time.
 *
 * Preferences (target language, behavior, never-translate list) persist
 * in localStorage so they apply across conversations.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type JSX } from 'react'
import { createPortal } from 'react-dom'
import {
  Check,
  ChevronDown,
  Copy,
  Languages,
  Loader2,
  Settings2,
  X,
} from 'lucide-react'

import type { Message } from '../../../modules/conversations/types.js'
import { plainToHtml } from './ai/text.js'
import { api } from '../../../api/client.js'

/* ────────────────────────────────────────────────────────────────────────── */
/*  Language list                                                             */
/* ────────────────────────────────────────────────────────────────────────── */

const TRANSLATE_LANGUAGES = [
  'Auto-detect',
  'English', 'Dutch', 'Spanish', 'French', 'German', 'Italian', 'Portuguese',
  'Hindi', 'Bengali', 'Tamil', 'Telugu', 'Marathi', 'Gujarati',
  'Arabic', 'Chinese (Simplified)', 'Japanese', 'Korean', 'Russian', 'Turkish',
  'Persian', 'Urdu', 'Indonesian', 'Malay', 'Filipino', 'Vietnamese', 'Thai',
  'Swahili', 'Hebrew', 'Polish', 'Ukrainian', 'Czech', 'Greek', 'Romanian',
  'Hungarian', 'Swedish', 'Norwegian', 'Danish', 'Finnish', 'Catalan',
  'Serbian', 'Croatian', 'Bulgarian', 'Slovak', 'Slovenian', 'Latvian',
  'Lithuanian', 'Estonian',
] as const

/* ────────────────────────────────────────────────────────────────────────── */
/*  Preferences (localStorage)                                                */
/* ────────────────────────────────────────────────────────────────────────── */

type TranslationBehavior = 'ask' | 'auto' | 'never'

interface TranslationPrefs {
  targetLanguage: string
  sourceLanguage: string
  behavior: TranslationBehavior
  neverTranslate: string[]
}

const DEFAULT_PREFS: TranslationPrefs = {
  targetLanguage: 'English',
  sourceLanguage: 'Auto-detect',
  behavior: 'ask',
  neverTranslate: [],
}

const PREFS_KEY = 'kc3-translation-prefs'

function loadPrefs(): TranslationPrefs {
  try {
    const raw = localStorage.getItem(PREFS_KEY)
    if (!raw) return DEFAULT_PREFS
    return { ...DEFAULT_PREFS, ...JSON.parse(raw) }
  } catch {
    return DEFAULT_PREFS
  }
}

function savePrefs(prefs: TranslationPrefs): void {
  try {
    localStorage.setItem(PREFS_KEY, JSON.stringify(prefs))
  } catch {
    /* ignore */
  }
}

/* ────────────────────────────────────────────────────────────────────────── */
/*  Types                                                                     */
/* ────────────────────────────────────────────────────────────────────────── */

interface TranslationData {
  text: string
  confidence: number | null
  grammar: number | null
  sourceLanguage: string
  targetLanguage: string
}

type ViewState = 'idle' | 'translating' | 'translated' | 'showing-original'

/* ────────────────────────────────────────────────────────────────────────── */
/*  Language selector dropdown                                                */
/* ────────────────────────────────────────────────────────────────────────── */

function LanguageSelect({
  value,
  onChange,
  label,
}: {
  value: string
  onChange: (v: string) => void
  label: string
}): JSX.Element {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent): void => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const filtered = useMemo(() => {
    if (!search.trim()) return TRANSLATE_LANGUAGES
    const q = search.toLowerCase()
    return TRANSLATE_LANGUAGES.filter((l) => l.toLowerCase().includes(q))
  }, [search])

  return (
    <div ref={ref} className="relative">
      <label className="mb-1 block text-[11px] font-medium text-slate-500">{label}</label>
      <button
        type="button"
        onClick={() => { setOpen((v) => !v); setSearch('') }}
        className="flex w-full items-center justify-between gap-2 rounded-[4px] border border-slate-200 bg-white px-3 py-2 text-xs text-slate-800 transition-colors hover:border-slate-300 focus:border-[#3366FF] focus:outline-none"
      >
        <span>{value}</span>
        <ChevronDown className={`h-3 w-3 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute left-0 top-full z-[9999] mt-1 w-full border border-slate-200 bg-white shadow-lg" style={{ borderRadius: '4px' }}>
          <div className="border-b border-slate-100 p-2">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search…"
              className="w-full rounded-[4px] border border-slate-200 px-2 py-1.5 text-xs text-slate-700 outline-none focus:border-[#3366FF]"
              autoFocus
            />
          </div>
          <div className="max-h-40 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <div className="px-3 py-2 text-xs text-slate-400">No language found</div>
            ) : (
              filtered.map((lang) => (
                <button
                  key={lang}
                  type="button"
                  onClick={() => { onChange(lang); setOpen(false) }}
                  className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs transition-colors hover:bg-blue-50 hover:text-blue-700 ${
                    lang === value ? 'font-semibold text-[#3366FF]' : 'text-slate-700'
                  }`}
                >
                  {lang}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  )
}

/* ────────────────────────────────────────────────────────────────────────── */
/*  Settings popover                                                          */
/* ────────────────────────────────────────────────────────────────────────── */

function SettingsPopover({
  prefs,
  onPrefsChange,
  onClose,
  position,
}: {
  prefs: TranslationPrefs
  onPrefsChange: (p: TranslationPrefs) => void
  onClose: () => void
  position: { top: number; left: number }
}): JSX.Element {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handler = (e: MouseEvent): void => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose()
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [onClose])

  return (
    <div
      ref={ref}
      className="fixed z-[99999] w-72 overflow-visible border border-slate-200 bg-white p-4 shadow-xl"
      style={{ borderRadius: '6px', top: position.top, left: position.left }}
    >
      <div className="mb-3 flex items-center justify-between">
        <span className="text-xs font-semibold text-slate-800">Translation settings</span>
        <button
          type="button"
          onClick={onClose}
          className="rounded-full p-0.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
          aria-label="Close settings"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      <div className="space-y-3">
        <LanguageSelect
          label="Source language"
          value={prefs.sourceLanguage}
          onChange={(v) => onPrefsChange({ ...prefs, sourceLanguage: v })}
        />
        <LanguageSelect
          label="Target language"
          value={prefs.targetLanguage}
          onChange={(v) => onPrefsChange({ ...prefs, targetLanguage: v })}
        />

        <div>
          <div className="mb-1.5 text-[11px] font-medium text-slate-500">Translation behavior</div>
          <div className="space-y-1.5">
            {([
              { value: 'ask', label: 'Ask me to translate' },
              { value: 'auto', label: 'Automatically translate' },
              { value: 'never', label: "Don't translate" },
            ] as const).map(({ value, label }) => (
              <label
                key={value}
                className="flex cursor-pointer items-center gap-2 text-xs text-slate-700"
              >
                <input
                  type="radio"
                  name="translation-behavior"
                  value={value}
                  checked={prefs.behavior === value}
                  onChange={() => onPrefsChange({ ...prefs, behavior: value })}
                  className="h-3.5 w-3.5 accent-[#3366FF]"
                />
                {label}
              </label>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

/* ────────────────────────────────────────────────────────────────────────── */
/*  Score bar                                                                 */
/* ────────────────────────────────────────────────────────────────────────── */

function ScoreBar({ label, score }: { label: string; score: number | null }): JSX.Element {
  const display = score != null ? `${score}%` : '—'
  const color = score == null ? 'bg-slate-200' : score >= 80 ? 'bg-emerald-500' : score >= 60 ? 'bg-amber-500' : 'bg-red-500'
  const textColor = score == null ? 'text-slate-400' : score >= 80 ? 'text-emerald-700' : score >= 60 ? 'text-amber-700' : 'text-red-700'
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-[10px] font-medium text-slate-500">{label}</span>
      <div className="h-1.5 w-16 overflow-hidden rounded-full bg-slate-100">
        <div className={`h-full rounded-full ${color}`} style={{ width: score != null ? `${score}%` : '0%' }} />
      </div>
      <span className={`text-[10px] font-semibold ${textColor}`}>{display}</span>
    </div>
  )
}

/* ────────────────────────────────────────────────────────────────────────── */
/*  Main component                                                            */
/* ────────────────────────────────────────────────────────────────────────── */

export interface TranslatableContentProps {
  message: Message
  /** Renders the original HTML content (typically <MessageBody html={...} />). */
  renderOriginal: (html: string) => JSX.Element
}

export default function TranslatableContent({
  message,
  renderOriginal,
}: TranslatableContentProps): JSX.Element {
  const [prefs, setPrefs] = useState<TranslationPrefs>(loadPrefs)
  const [view, setView] = useState<ViewState>('idle')
  const [translation, setTranslation] = useState<TranslationData | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [showLangDropdown, setShowLangDropdown] = useState(false)
  const [showSettings, setShowSettings] = useState(false)
  const [copied, setCopied] = useState(false)
  const [langSearch, setLangSearch] = useState('')
  const cacheRef = useRef<Map<string, TranslationData>>(new Map())
  const settingsBtnRef = useRef<HTMLButtonElement>(null)
  const [popoverPos, setPopoverPos] = useState<{ top: number; left: number } | null>(null)
  const langBtnRef = useRef<HTMLButtonElement>(null)
  const [langDropdownPos, setLangDropdownPos] = useState<{ top: number; left: number } | null>(null)

  useEffect(() => {
    setPrefs(loadPrefs())
  }, [])

  const updatePrefs = useCallback((p: TranslationPrefs): void => {
    setPrefs(p)
    savePrefs(p)
  }, [])

  const openSettings = useCallback((btnEl?: HTMLElement | null): void => {
    const el = btnEl ?? settingsBtnRef.current
    if (el) {
      const rect = el.getBoundingClientRect()
      setPopoverPos({ top: rect.bottom + 4, left: rect.right - 288 })
    }
    setShowSettings(true)
  }, [])

  const closeSettings = useCallback((): void => {
    setShowSettings(false)
    setPopoverPos(null)
  }, [])

  const toggleLangDropdown = useCallback((btnEl?: HTMLElement | null): void => {
    setShowLangDropdown((prev) => {
      const el = btnEl ?? langBtnRef.current
      if (!prev && el) {
        const rect = el.getBoundingClientRect()
        setLangDropdownPos({ top: rect.bottom + 4, left: rect.left })
      }
      return !prev
    })
    setLangSearch('')
  }, [])

  // Track previous language values to detect changes
  const prevTargetLang = useRef(prefs.targetLanguage)
  const prevSourceLang = useRef(prefs.sourceLanguage)

  const doTranslate = useCallback(async (targetLang: string): Promise<void> => {
    const sourceLang = prefs.sourceLanguage
    const cacheKey = `${message.id}:${sourceLang}:${targetLang}`
    const cached = cacheRef.current.get(cacheKey)
    if (cached) {
      setTranslation(cached)
      setView('translated')
      setError(null)
      setShowLangDropdown(false)
      setLangSearch('')
      return
    }

    setView('translating')
    setError(null)
    setShowLangDropdown(false)
    setLangSearch('')

    // Set placeholder so the skeleton/loading state renders before API resolves
    setTranslation({
      text: '',
      confidence: null,
      grammar: null,
      sourceLanguage: sourceLang === 'Auto-detect' ? 'Auto' : sourceLang,
      targetLanguage: targetLang,
    })

    try {
      const res = await api.post<{
        translatedText: string
        confidence: number | null
        grammar: number | null
      }>('/agent/ai/translate', {
        text: message.body || '',
        targetLanguage: targetLang,
      })
      const data: TranslationData = {
        text: res.translatedText,
        confidence: res.confidence,
        grammar: res.grammar,
        sourceLanguage: sourceLang === 'Auto-detect' ? 'Auto' : sourceLang,
        targetLanguage: targetLang,
      }
      cacheRef.current.set(cacheKey, data)
      setTranslation(data)
      setView('translated')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Translation failed')
      setView('idle')
    }
  }, [message.id, message.body, prefs.sourceLanguage])

  // Auto-retranslate when target or source language changes in settings
  // while a translation is already active (translated or showing-original).
  useEffect(() => {
    if (prevTargetLang.current === prefs.targetLanguage && prevSourceLang.current === prefs.sourceLanguage) return
    prevTargetLang.current = prefs.targetLanguage
    prevSourceLang.current = prefs.sourceLanguage

    if (view === 'translated' || view === 'showing-original') {
      void doTranslate(prefs.targetLanguage)
    }
  }, [prefs.targetLanguage, prefs.sourceLanguage, view, doTranslate])

  const handleCopy = useCallback((): void => {
    if (!translation?.text) return
    void navigator.clipboard?.writeText(translation.text)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }, [translation?.text])

  const filteredLanguages = useMemo(() => {
    const langs = TRANSLATE_LANGUAGES.filter((l) => l !== 'Auto-detect')
    if (!langSearch.trim()) return langs
    const q = langSearch.toLowerCase()
    return langs.filter((l) => l.toLowerCase().includes(q))
  }, [langSearch])

  const showingTranslated = view === 'translated' || view === 'translating'
  const showingOriginal = view === 'showing-original'

  return (
    <div className="relative">
      {/* ── Translation banner (shown when translation is active) ── */}
      {(showingTranslated || showingOriginal) && translation && (
        <div
          className="mb-2 flex items-center justify-between gap-2 rounded-[4px] border border-slate-200 bg-slate-50/80 px-3 py-2"
        >
          <div className="flex items-center gap-2">
            <Languages className="h-3.5 w-3.5 text-slate-500" />
            <span className="text-[11px] font-medium text-slate-700">
              Translated: {translation.sourceLanguage} → {translation.targetLanguage}
            </span>
            <div>
              <button
                ref={settingsBtnRef}
                type="button"
                onClick={() => showSettings ? closeSettings() : openSettings()}
                className="rounded-full p-1 text-slate-400 transition-colors hover:bg-slate-200 hover:text-slate-600"
                aria-label="Translation settings"
                title="Translation settings"
              >
                <Settings2 className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Change language dropdown — accessible after first translation */}
            <div className="flex items-center">
              <button
                type="button"
                onClick={(e) => toggleLangDropdown(e.currentTarget)}
                className="inline-flex items-center gap-1 rounded-[3px] px-1.5 py-0.5 text-[10px] font-medium text-slate-500 transition-colors hover:bg-slate-200 hover:text-slate-700"
                title="Change language"
              >
                <Languages className="h-3 w-3" />
                Change
                <ChevronDown className={`h-2.5 w-2.5 transition-transform ${showLangDropdown ? 'rotate-180' : ''}`} />
              </button>
            </div>
            <button
              type="button"
              onClick={handleCopy}
              className="inline-flex items-center gap-1 rounded-[3px] px-1.5 py-0.5 text-[10px] font-medium text-slate-500 transition-colors hover:bg-slate-200 hover:text-slate-700"
              title="Copy translated text"
            >
              {copied ? (
                <><Check className="h-3 w-3 text-emerald-600" /> Copied</>
              ) : (
                <><Copy className="h-3 w-3" /> Copy</>
              )}
            </button>
            {showingTranslated && (
              <button
                type="button"
                onClick={() => setView('showing-original')}
                className="text-[11px] font-medium text-[#3366FF] transition-colors hover:text-[#2952cc] hover:underline"
              >
                Show original
              </button>
            )}
            {showingOriginal && (
              <button
                type="button"
                onClick={() => setView('translated')}
                className="text-[11px] font-medium text-[#3366FF] transition-colors hover:text-[#2952cc] hover:underline"
              >
                Show translation
              </button>
            )}
            <button
              type="button"
              onClick={() => { setView('idle'); setTranslation(null); setShowSettings(false) }}
              className="rounded-full p-0.5 text-slate-400 transition-colors hover:bg-slate-200 hover:text-slate-600"
              title="Close translation"
              aria-label="Close translation"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        </div>
      )}

      {/* Settings popover — rendered via portal to escape overflow-hidden ancestors */}
      {showSettings && (showingTranslated || showingOriginal) && translation && popoverPos && createPortal(
        <SettingsPopover
          prefs={prefs}
          onPrefsChange={updatePrefs}
          onClose={closeSettings}
          position={popoverPos}
        />,
        document.body
      )}

      {/* ── Content area: either original or translated, never both ── */}
      {showingTranslated && translation ? (
        <div>
          {view === 'translating' && (
            <div className="space-y-2 py-2">
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <Loader2 className="h-3.5 w-3.5 animate-spin text-[#3366FF]" />
                Translating to {translation.targetLanguage}…
              </div>
              {/* Skeleton lines mimicking message content */}
              <div className="space-y-2.5">
                <div className="h-3.5 w-full animate-pulse rounded-[3px] bg-slate-200/70" />
                <div className="h-3.5 w-[95%] animate-pulse rounded-[3px] bg-slate-200/70" />
                <div className="h-3.5 w-[88%] animate-pulse rounded-[3px] bg-slate-200/70" />
                <div className="h-3.5 w-[92%] animate-pulse rounded-[3px] bg-slate-200/70" />
                <div className="h-3.5 w-[70%] animate-pulse rounded-[3px] bg-slate-200/70" />
              </div>
              {/* Skeleton score bars */}
              <div className="flex items-center gap-4 pt-1">
                <div className="flex items-center gap-1.5">
                  <div className="h-2.5 w-16 animate-pulse rounded-full bg-slate-200/70" />
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="h-2.5 w-16 animate-pulse rounded-full bg-slate-200/70" />
                </div>
              </div>
            </div>
          )}
          {view === 'translated' && (
            <>
              <div
                className="kc3-message-html text-sm leading-relaxed text-slate-800"
                dangerouslySetInnerHTML={{ __html: plainToHtml(translation.text) }}
              />
              {(translation.confidence != null || translation.grammar != null) && (
                <div className="mt-2 flex flex-wrap items-center gap-4 border-t border-slate-100 pt-2">
                  <ScoreBar label="Confidence" score={translation.confidence} />
                  <ScoreBar label="Grammar" score={translation.grammar} />
                </div>
              )}
            </>
          )}
        </div>
      ) : showingOriginal && translation ? (
        <div>
          {message.body ? (
            renderOriginal(message.body)
          ) : (
            <div className="text-sm italic text-slate-400">No message content</div>
          )}
          <p className="mt-1.5 text-[10px] text-slate-400">
            Showing original content
          </p>
        </div>
      ) : (
        <>
          {/* Translate button + language dropdown — ABOVE the content */}
          {message.body && (
            <div className="mb-2">
              <div className="flex items-center gap-2">
                <button
                  ref={langBtnRef}
                  type="button"
                  onClick={(e) => toggleLangDropdown(e.currentTarget)}
                  className="inline-flex items-center gap-1.5 rounded-[4px] border border-slate-200 bg-white px-2.5 py-1 text-xs font-medium text-slate-600 transition-colors hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900"
                >
                  <Languages className="h-3.5 w-3.5 text-[#3366FF]" />
                  Translate to {prefs.targetLanguage}
                  <ChevronDown className={`h-3 w-3 transition-transform ${showLangDropdown ? 'rotate-180' : ''}`} />
                </button>

                {/* Quick translate to default target language */}
                <button
                  type="button"
                  onClick={() => void doTranslate(prefs.targetLanguage)}
                  className="inline-flex items-center gap-1 rounded-[4px] px-2 py-1 text-xs font-medium text-[#3366FF] transition-colors hover:bg-[#3366FF]/8"
                >
                  <Languages className="h-3 w-3" />
                  Translate now
                </button>
              </div>
            </div>
          )}

          {/* Original content (idle state) */}
          {message.body ? (
            renderOriginal(message.body)
          ) : (
            <div className="text-sm italic text-slate-400">No message content</div>
          )}
        </>
      )}

      {/* Error display */}
      {error && (
        <div className="mt-2 rounded-[4px] border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-600">
          {error}
        </div>
      )}

      {/* Language dropdown — rendered via portal to escape overflow-hidden ancestors */}
      {showLangDropdown && langDropdownPos && createPortal(
        <div className="fixed z-[99999] w-60 border border-slate-200 bg-white shadow-xl" style={{ borderRadius: '4px', top: langDropdownPos.top, left: langDropdownPos.left }}>
          <div className="border-b border-slate-100 p-2">
            <input
              type="text"
              value={langSearch}
              onChange={(e) => setLangSearch(e.target.value)}
              placeholder="Search language…"
              className="w-full rounded-[4px] border border-slate-200 px-2 py-1.5 text-xs text-slate-700 outline-none focus:border-[#3366FF]"
              autoFocus
            />
          </div>
          <div className="max-h-52 overflow-y-auto py-1">
            {filteredLanguages.length === 0 ? (
              <div className="px-3 py-2 text-xs text-slate-400">No language found</div>
            ) : (
              filteredLanguages.map((lang) => (
                <button
                  key={lang}
                  type="button"
                  onClick={() => { void doTranslate(lang); setShowLangDropdown(false) }}
                  className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-slate-700 transition-colors hover:bg-blue-50 hover:text-blue-700"
                >
                  {lang}
                  {lang === prefs.targetLanguage && (
                    <Check className="ml-auto h-3 w-3 text-[#3366FF]" />
                  )}
                </button>
              ))
            )}
          </div>
          <div className="border-t border-slate-100 p-1.5">
            <button
              type="button"
              onClick={(e) => { setShowLangDropdown(false); openSettings(e.currentTarget) }}
              className="flex w-full items-center gap-2 px-2 py-1.5 text-left text-xs text-slate-500 transition-colors hover:bg-slate-50 hover:text-slate-700"
            >
              <Settings2 className="h-3 w-3" />
              Translation settings
            </button>
          </div>
        </div>,
        document.body
      )}

      {/* Settings popover — rendered via portal for idle state */}
      {showSettings && !showingTranslated && !showingOriginal && popoverPos && createPortal(
        <SettingsPopover
          prefs={prefs}
          onPrefsChange={updatePrefs}
          onClose={closeSettings}
          position={popoverPos}
        />,
        document.body
      )}
    </div>
  )
}
